import { getLabelMessage } from '@gef-ui/localization/messages';
import Eye from '@gef-ui/material-icons/Eye';
import EyeSlash from '@gef-ui/material-icons/EyeSlash';
import GenericDialog from '@gef-ui/material/components/Dialog/GenericDialog';
import { useVersionInfoLazy } from '@gef-ui/version-info-core';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { deleteSent, hideError, hideErrors, removeError, showErrors } from '../../actions';
import { useErrorContext, useErrorDispatch } from '../../context';
import { useSendErrorsContext } from '../../hooks';
import { useDefaultComponentValue } from '../../utils';
import { ReportButton } from '../ActionButtons';
import ContextCollapsibleErrorExport from '../ContextCollapsibleError';
const ContextCollapsibleError = styled(ContextCollapsibleErrorExport)(({ theme })=>({
        padding: theme.spacing(2, 0, 3)
    }));
const NoErrorsText = styled(Typography)(({ theme })=>({
        padding: theme.spacing(2, 2.5, 0),
        width: '100%',
        textAlign: 'center',
        display: 'block',
        // todo varianta ??
        fontSize: 20
    }));
const Buttons = styled(Stack)(({ theme })=>({
        flexDirection: 'row',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            rowGap: theme.spacing(1.5),
            flexGrow: 1
        }
    }));
const HideAllButton = styled(Button)(({ theme })=>({
        [theme.breakpoints.up('md')]: {
            marginLeft: theme.spacing(1)
        }
    }));
const COMPONENT_NAME = 'MuiModalMultipleErrors';
const ModalMultipleErrors = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { onCloseModal, onRefreshError, withSendOption, versionInfoConfig, sendErrorCallback, ...other } = props;
    const errorContext = useErrorContext();
    const dispatch = useErrorDispatch();
    const { fe, be } = useVersionInfoLazy(versionInfoConfig);
    const { errors } = errorContext;
    const visibleErrors = errors.filter(({ hidden })=>!hidden);
    const allErrorsSent = errors.every((error)=>error.sentSuccess || error.sentFail);
    const { isSending, sendErrors } = useSendErrorsContext(sendErrorCallback);
    const sendAllErrors = ()=>{
        sendErrors(fe, be, errors);
    };
    const hasVisibleErrors = visibleErrors.length !== 0;
    const hasHiddenErrors = errors.filter(({ hidden })=>!!hidden).length !== 0;
    return /*#__PURE__*/ React.createElement(GenericDialog, {
        TransitionProps: {
            onExited: ()=>dispatch(deleteSent())
        },
        withCross: true,
        fullWidth: true,
        maxWidth: "lg",
        title: getLabelMessage('errors:overviewOfErrors'),
        onExit: onCloseModal,
        DialogContentProps: {
            sx: {
                pb: 0,
                '&.MuiDialogContent-root': {
                    pt: 0
                }
            }
        },
        DialogActionsProps: {
            sx: {
                pt: 2,
                pb: 2
            }
        },
        CrossButtonProps: {
            id: 'close-error-cross'
        },
        content: /*#__PURE__*/ React.createElement(Stack, {
            sx: {
                flexDirection: 'column'
            }
        }, !hasVisibleErrors ? /*#__PURE__*/ React.createElement(NoErrorsText, null, getLabelMessage('errors:noVisibleErrors')) : /*#__PURE__*/ React.createElement(Box, {
            sx: {
                overflow: 'auto'
            }
        }, visibleErrors.map((error)=>{
            return /*#__PURE__*/ React.createElement(React.Fragment, {
                key: error.uuid
            }, /*#__PURE__*/ React.createElement(ContextCollapsibleError, {
                sendErrorCallback: sendErrorCallback,
                withSendOption: withSendOption,
                error: error,
                isSending: isSending,
                onRefresh: onRefreshError ? ()=>{
                    onRefreshError(error);
                    dispatch(removeError(error));
                } : undefined,
                onHide: ()=>dispatch(hideError(error))
            }), /*#__PURE__*/ React.createElement(Divider, null));
        }))),
        actions: /*#__PURE__*/ React.createElement(Buttons, null, hasVisibleErrors && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ReportButton, {
            withSendOption: withSendOption,
            onSend: sendAllErrors,
            isSent: allErrorsSent,
            isSending: isSending,
            fe: fe,
            be: be,
            errors: visibleErrors
        }), /*#__PURE__*/ React.createElement(HideAllButton, {
            startIcon: /*#__PURE__*/ React.createElement(EyeSlash, {
                sx: (theme)=>({
                        fill: theme.palette.primary.main
                    })
            }),
            onClick: ()=>dispatch(hideErrors())
        }, getLabelMessage('errors:hideAll'))), hasHiddenErrors && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(HideAllButton, {
            startIcon: /*#__PURE__*/ React.createElement(Eye, {
                sx: (theme)=>({
                        fill: theme.palette.primary.main
                    })
            }),
            onClick: ()=>dispatch(showErrors())
        }, getLabelMessage('errors:showAll')))),
        ...other
    });
};
ModalMultipleErrors.propTypes = {
    onCloseModal: PropTypes.func,
    onRefreshError: PropTypes.func,
    /**
	 * Custom callback for sending errors to BE called with HTTP body
	 */ sendErrorCallback: PropTypes.func,
    versionInfoConfig: PropTypes.object,
    withSendOption: PropTypes.bool
};
export default ModalMultipleErrors;
